import React, { useState, useEffect } from 'react';
import { 
  Row, Col, Card, Form, Input, Select, Button, 
  Typography, Space, Avatar, Divider, message, Upload, Tooltip,
  Checkbox, Modal, Tabs, InputNumber, Grid
} from 'antd';
import { 
  FacebookFilled, 
  PictureOutlined, 
  LinkOutlined, 
  SendOutlined,
  GlobalOutlined,
  EllipsisOutlined,
  EditOutlined,
  LikeOutlined,
  CommentOutlined,
  ShareAltOutlined,
  UploadOutlined,
  PlayCircleOutlined,
  CloseCircleFilled,
  PlusOutlined,
  DeleteOutlined,
  FileTextOutlined,
  CameraOutlined,
  InboxOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { pagesService } from '../../services/pages.service';
import { postsService } from '../../services/posts.service';
import { mediaService } from '../../services/media.service';
import { autoPostService } from '../../services/autopost.service';
import { Page, PostType, AutoPostRule } from '../../types';
import PageSelect from '../../components/Shared/PageSelect';
import { useLocation, useNavigate } from 'react-router-dom';
import config from '../../config';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;

interface CommentItem {
  text: string;
  image?: File;
  previewUrl?: string;
}

interface FileItem {
  uid: string;
  file: File;
  previewUrl: string;
  type: 'image' | 'video';
}

const CreatePost: React.FC = () => {
  const [form] = Form.useForm();
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  
  // Local files state
  const [fileList, setFileList] = useState<FileItem[]>([]);
  
  // Preview states
  const [previewContent, setPreviewContent] = useState({
    message: '',
    type: PostType.TEXT,
    mediaUrl: '',
    mediaUrls: [] as string[],
    link: '',
    selectedPage: null as Page | null
  });

  const [remoteMediaUrls, setRemoteMediaUrls] = useState<string[]>([]);
  const [urlInputValue, setUrlInputValue] = useState('');
  const [autoPostRules, setAutoPostRules] = useState<AutoPostRule[]>([]);
  
  // Auto interaction states
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [autoLike, setAutoLike] = useState(false);
  const [autoComment, setAutoComment] = useState(false);
  const [comments, setComments] = useState<CommentItem[]>([{ text: '' }]);
  const [commentDelay, setCommentDelay] = useState(10);
  const [importText, setImportText] = useState('');
  const [importedFileName, setImportedFileName] = useState<string | null>(null);

  const addCommentField = () => setComments([...comments, { text: '' }]);
  const removeCommentField = (index: number) => {
    const item = comments[index];
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    const newComments = comments.filter((_, i) => i !== index);
    setComments(newComments.length > 0 ? newComments : [{ text: '' }]);
  };
  const updateComment = (index: number, text: string) => {
    const newComments = [...comments];
    newComments[index] = { ...newComments[index], text };
    setComments(newComments);
  };

  const handleCommentImageUpload = (index: number, file: File) => {
    const newComments = [...comments];
    const previewUrl = URL.createObjectURL(file);
    if (newComments[index].previewUrl) URL.revokeObjectURL(newComments[index].previewUrl!);
    newComments[index] = { ...newComments[index], image: file, previewUrl };
    setComments(newComments);
    return false;
  };

  const downloadSampleExcel = () => {
    const data = [
      ["Bình luận"],
      ["Sản phẩm tuyệt vời!"],
      ["Shop phục vụ rất tốt"],
      ["Giao hàng nhanh chóng"],
      ["Chúc shop buôn may bán đắt nhé!"]
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    ws['!cols'] = [{ wch: 50 }];
    
    if (ws['A1']) {
      ws['A1'].s = {
        font: {
          bold: true,
          sz: 12,
          color: { rgb: "000000" }
        },
        fill: {
          fgColor: { rgb: "F2F2F2" }
        },
        alignment: {
          horizontal: "center",
          vertical: "center"
        },
        border: {
          bottom: { style: "thin", color: { rgb: "000000" } }
        }
      };
    }
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Danh sách bình luận");
    XLSX.writeFile(wb, "mau_binh_luan.xlsx");
  };

  const handleExcelImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];
        if (rows.length === 0) {
          message.warning('File Excel không có dữ liệu');
          return;
        }

        // Hàm xóa dấu tiếng Việt để so sánh chính xác hơn
        const normalize = (str: any) => {
          if (!str) return '';
          return String(str)
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[đĐ]/g, 'd')
            .trim();
        };

        const targetHeader = normalize("Bình luận");
        const headerRow = rows[0];
        const colIndex = headerRow.findIndex(cell => normalize(cell) === targetHeader);

        if (colIndex === -1) {
          const foundHeaders = headerRow.filter(h => h).join(', ');
          message.error(`Không tìm thấy cột "Bình luận". Các cột tìm thấy: [${foundHeaders}]`);
          return;
        }

        const importedComments: CommentItem[] = rows
          .slice(1)
          .map(row => row[colIndex])
          .filter(val => val && String(val).trim())
          .map(val => ({ text: String(val).trim() }));

        if (importedComments.length > 0) {
          setComments(importedComments);
          setImportedFileName(file.name);
          message.success(`Đã nhập ${importedComments.length} bình luận từ file "${file.name}"`);
        } else {
          message.warning('Cột "Bình luận" không có nội dung');
        }
      } catch (err) {
        console.error(err);
        message.error('Lỗi khi đọc file Excel');
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPages();
    fetchAutoPostRules();

    // Check for pre-filled data from location state (for "Copy & Edit")
    if (location.state) {
      const { message: msg, type, mediaUrl, mediaUrls, link, pageIds } = location.state;
      form.setFieldsValue({
        message: msg,
        type: type || PostType.TEXT,
        mediaUrl: mediaUrl,
        link: link,
        pageIds: pageIds || []
      });
      
      setPreviewContent(prev => ({
        ...prev,
        message: msg || '',
        type: type || PostType.TEXT,
        mediaUrl: mediaUrl || '',
        mediaUrls: mediaUrls || [],
        link: link || ''
      }));

      if (mediaUrls && mediaUrls.length > 0) {
        setRemoteMediaUrls(mediaUrls);
      }
    }
  }, [location.state, form]);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const data = await pagesService.getAll();
      setPages(data);
    } catch (error) {
      console.error(error);
      message.error('Không thể tải danh sách trang');
    } finally {
      setLoading(false);
    }
  };

  const fetchAutoPostRules = async () => {
    try {
      const rules = await autoPostService.getAll();
      setAutoPostRules(rules.filter(r => r.isActive));
    } catch (error) {
      console.error('Failed to fetch auto post rules', error);
    }
  };

  const selectedPageIds = Form.useWatch('pageIds', form) || [];

  const isPageDisabled = (pageId: string) => {
    for (const id of selectedPageIds) {
      if (id === pageId) return false;

      const isTargetOfSelected = autoPostRules.some(r => 
        r.sourcePageId === id && (Array.isArray(r.targetPageIds) ? r.targetPageIds.includes(pageId) : r.targetPageIds === pageId)
      );
      if (isTargetOfSelected) {
        return {
          disabled: true,
          message: `Trang "${pages.find(p => p.id === id)?.name}" đã chọn đang đẩy bài tự động sang trang này`
        };
      }

      const isSourceOfSelected = autoPostRules.some(r => 
        (r.sourcePageId === pageId) && (Array.isArray(r.targetPageIds) ? r.targetPageIds.includes(id) : r.targetPageIds === id)
      );
      if (isSourceOfSelected) {
        return {
          disabled: true,
          message: `Trang này đang đẩy bài tự động sang trang "${pages.find(p => p.id === id)?.name}" đã chọn`
        };
      }
    }
    return false;
  };

  const handleValuesChange = (changedValues: any, allValues: any) => {
    // For preview, we use the first selected page
    const firstPageId = allValues.pageIds?.[0];
    const selectedPage = pages.find(p => p.id === firstPageId) || null;
    
    // If type changed, clear files if necessary
    if (changedValues.type) {
        setFileList([]);
        setRemoteMediaUrls([]);
    }

    setPreviewContent({
      message: allValues.message || '',
      type: allValues.type || PostType.TEXT,
      mediaUrl: allValues.mediaUrl || '',
      mediaUrls: remoteMediaUrls,
      link: allValues.link || '',
      selectedPage
    });
  };

  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    
    if (!isImage && !isVideo) {
      message.error('Chỉ được phép tải lên hình ảnh hoặc video!');
      return Upload.LIST_IGNORE;
    }

    const type = isImage ? 'image' : 'video';
    
    // If current type is video, only allow one video
    if (previewContent.type === PostType.VIDEO && fileList.length > 0) {
        message.warning('Chỉ có thể chọn 1 video cho mỗi bài viết');
        return Upload.LIST_IGNORE;
    }

    const newItem: FileItem = {
      uid: Math.random().toString(36).substring(7),
      file,
      previewUrl: URL.createObjectURL(file),
      type: type as 'image' | 'video'
    };

    if (previewContent.type === PostType.VIDEO) {
        setFileList([newItem]);
    } else {
        setFileList(prev => [...prev, newItem]);
    }
    
    setTimeout(() => form.validateFields(['message']), 0);
    
    return false; // Prevent auto upload
  };

  const removeFile = (uid: string) => {
    setFileList(prev => {
        const item = prev.find(f => f.uid === uid);
        if (item) URL.revokeObjectURL(item.previewUrl);
        const newList = prev.filter(f => f.uid !== uid);
        setTimeout(() => form.validateFields(['message']), 0);
        return newList;
    });
  };

  const onFinish = async (values: any) => {
    const isMediaPost = [PostType.PHOTO, PostType.VIDEO].includes(values.type);
    const hasMedia = fileList.length > 0 || !!values.mediaUrl || remoteMediaUrls.length > 0 || !!urlInputValue;

    if (isMediaPost && !hasMedia) {
        message.error('Vui lòng chọn tệp hoặc nhập URL');
        return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      
      if (Array.isArray(values.pageIds)) {
        values.pageIds.forEach((id: string) => {
          formData.append('pageIds', id);
        });
      }

      formData.append('message', values.message);
      formData.append('type', values.type);
      
      if (values.mediaUrl) formData.append('mediaUrl', values.mediaUrl);
      if (values.link) formData.append('link', values.link);
      
      // Append remote media URLs (from Facebook) - include the one currently in the input box if any
      const finalRemoteUrls = [...remoteMediaUrls];
      if (urlInputValue) finalRemoteUrls.push(urlInputValue);
      
      finalRemoteUrls.forEach(url => {
        formData.append('mediaUrls', url);
      });

      // Append files
      fileList.forEach(item => {
        formData.append('files', item.file);
      });
      
      // Auto interaction
      formData.append('autoLike', String(autoLike));
      formData.append('autoComment', String(autoComment));
      if (autoComment) {
        const commentsData = comments
          .filter(c => c.text.trim())
          .map((c, index) => {
            if (c.image) {
              formData.append(`comment_image_${index}`, c.image);
            }
            return c.text.trim();
          });
          
        formData.append('comments', JSON.stringify(commentsData));
        formData.append('commentDelay', String(commentDelay));
      }

      // Send to API
      await postsService.create(formData);

      message.success('Đăng bài thành công!');
      form.resetFields(['message', 'mediaUrl', 'link']);
      setFileList([]);
      setPreviewContent(prev => ({ ...prev, message: '', mediaUrl: '', link: '' }));
      // Clear validation
      form.setFields([{ name: 'message', errors: [] }]);
    } catch (error: any) {
      console.error(error);
      if (error.response && error.response.data && error.response.data.message) {
          message.error(error.response.data.message);
      } else {
          message.error('Đăng bài thất bại');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: isMobile ? '12px' : '24px' }}>
      <Title level={isMobile ? 4 : 3} style={{ marginBottom: 24 }}>Tạo bài viết mới</Title>
      
      <Row gutter={[32, 32]}>
        {/* Left Side: Form */}
        <Col xs={24} lg={12}>
          <Card 
            title={<Space><FacebookFilled style={{ color: '#1877F2' }} /><span>Nội dung bài viết</span></Space>}
            bordered={false}
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: 12 }}
            bodyStyle={{ padding: isMobile ? '16px' : '24px' }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              onValuesChange={handleValuesChange}
              initialValues={{ type: PostType.TEXT }}
            >
              <Form.Item 
                name="pageIds" 
                label="Đăng lên các trang" 
                rules={[{ required: true, message: 'Vui lòng chọn ít nhất một trang' }]}
              >
                <PageSelect 
                  placeholder="Chọn các trang quản lý" 
                  loading={loading}
                  mode="multiple"
                  pages={pages}
                  isPageDisabled={isPageDisabled}
                />
              </Form.Item>

              <Form.Item 
                name="message" 
                label="Bạn đang nghĩ gì?" 
                rules={[
                  {
                    validator: async (_, value) => {
                      const type = form.getFieldValue('type');
                      const mediaUrl = form.getFieldValue('mediaUrl');
                      const link = form.getFieldValue('link');
                      
                      const hasValue = !!value?.trim();
                      const hasMedia = !!(mediaUrl || link || fileList.length > 0);
                      
                      if (!hasValue && !hasMedia) {
                        return Promise.reject(new Error('Vui lòng nhập nội dung hoặc đính kèm tệp/link'));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <TextArea 
                  rows={isMobile ? 4 : 6} 
                  placeholder="Nhập nội dung bài viết ở đây..." 
                  style={{ borderRadius: 8, fontSize: isMobile ? 14 : 16 }}
                />
              </Form.Item>

              <Form.Item name="type" label="Loại bài đăng">
                <Select style={{ borderRadius: 8 }}>
                  <Option value={PostType.TEXT}>Chỉ văn bản</Option>
                  <Option value={PostType.PHOTO}>Hình ảnh</Option>
                  <Option value={PostType.VIDEO}>Video</Option>
                  <Option value={PostType.LINK}>Liên kết</Option>
                </Select>
              </Form.Item>

              <Form.Item 
                noStyle 
                shouldUpdate={(prev, current) => prev.type !== current.type}
              >
                {({ getFieldValue }) => {
                  const type = getFieldValue('type');
                  if (type === PostType.PHOTO || type === PostType.VIDEO) {
                    return (
                      <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8, border: '1px dashed #d9d9d9' }}>
                        <Text strong style={{ display: 'block', marginBottom: 12 }}>
                            {type === PostType.PHOTO ? 'Tải ảnh lên (có thể chọn nhiều)' : 'Tải video lên'}
                        </Text>
                        
                        <Upload
                          multiple={type === PostType.PHOTO}
                          beforeUpload={beforeUpload}
                          showUploadList={false}
                          accept={type === PostType.PHOTO ? "image/*" : "video/*"}
                        >
                          <Button icon={<UploadOutlined />}>Chọn tệp từ máy tính</Button>
                        </Upload>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
                            {/* Local files */}
                            {fileList.map(item => (
                                <div key={item.uid} style={{ position: 'relative', width: 80, height: 80, border: '1px solid #ddd', borderRadius: 4, overflow: 'hidden' }}>
                                    {item.type === 'image' ? (
                                        <img src={item.previewUrl} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="preview" />
                                    ) : (
                                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
                                            <PlayCircleOutlined style={{ color: '#fff', fontSize: 24 }} />
                                        </div>
                                    )}
                                    <CloseCircleFilled 
                                        style={{ position: 'absolute', top: 2, right: 2, color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 16 }}
                                        onClick={() => removeFile(item.uid)}
                                    />
                                </div>
                            ))}
                            {/* Remote media (e.g. from Facebook) */}
                            {remoteMediaUrls.map((url, idx) => (
                                <div key={idx} style={{ position: 'relative', width: 80, height: 80, border: '1px solid #ddd', borderRadius: 4, overflow: 'hidden' }}>
                                    <img src={url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="remote-preview" />
                                    <CloseCircleFilled 
                                        style={{ position: 'absolute', top: 2, right: 2, color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 16 }}
                                        onClick={() => setRemoteMediaUrls(prev => prev.filter((_, i) => i !== idx))}
                                    />
                                </div>
                            ))}
                        </div>

                        <Divider plain>Hoặc</Divider>

                        <div style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                            Nhập URL ảnh/video và nhấn Enter hoặc nút Thêm:
                          </Text>
                          <Space.Compact style={{ width: '100%' }}>
                            <Input 
                              prefix={type === PostType.PHOTO ? <PictureOutlined /> : <PlayCircleOutlined />} 
                              placeholder={type === PostType.PHOTO ? "https://example.com/image.jpg" : "https://example.com/video.mp4"}
                              style={{ borderRadius: '8px 0 0 8px' }}
                              value={urlInputValue}
                              onChange={e => setUrlInputValue(e.target.value)}
                              onPressEnter={(e: any) => {
                                e.preventDefault();
                                if (urlInputValue) {
                                  setRemoteMediaUrls(prev => [...prev, urlInputValue]);
                                  setUrlInputValue('');
                                }
                              }}
                            />
                            <Button 
                              style={{ borderRadius: '0 8px 8px 0' }}
                              onClick={() => {
                                if (urlInputValue) {
                                  setRemoteMediaUrls(prev => [...prev, urlInputValue]);
                                  setUrlInputValue('');
                                }
                              }}
                            >Thêm</Button>
                          </Space.Compact>
                        </div>
                      </div>
                    );
                  }
                  if (type === PostType.LINK) {
                    return (
                      <Form.Item 
                        name="link" 
                        label="Đường dẫn liên kết" 
                        rules={[{ required: true, message: 'Vui lòng nhập URL' }]}
                      >
                        <Input 
                          prefix={<LinkOutlined />} 
                          placeholder="https://facebook.com/..." 
                          style={{ borderRadius: 8 }}
                        />
                      </Form.Item>
                    );
                  }
                  return null;
                }}
              </Form.Item>

              <Divider />
              
              <div style={{ marginBottom: 20 }}>
                <Text strong style={{ display: 'block', marginBottom: 12 }}>Tự động tương tác:</Text>
                <Space size="large">
                  <Checkbox 
                    checked={autoLike} 
                    onChange={e => setAutoLike(e.target.checked)}
                  >
                    Tự động Like
                  </Checkbox>
                  <Space>
                    <Checkbox 
                      checked={autoComment} 
                      onChange={e => {
                        setAutoComment(e.target.checked);
                        if (e.target.checked) setShowCommentModal(true);
                      }}
                    >
                      Tự động Bình luận
                    </Checkbox>
                    {autoComment && (
                      <Button 
                        size="small" 
                        type="link" 
                        icon={<EditOutlined />} 
                        onClick={() => setShowCommentModal(true)}
                        style={{ padding: 0 }}
                      >
                        Cài đặt
                      </Button>
                    )}
                  </Space>
                </Space>
              </div>

              <Divider />

              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SendOutlined />} 
                loading={submitting}
                block 
                size="large"
                style={{ borderRadius: 8, height: 48, fontWeight: 600 }}
              >
                Đăng lên Facebook
              </Button>
            </Form>
          </Card>
        </Col>

        {/* Right Side: Preview */}
        <Col xs={24} lg={12}>
          <div style={{ position: 'sticky', top: 24 }}>
            <Title level={4} style={{ marginBottom: 16, textAlign: 'center', color: '#65676B' }}>Bản xem trước</Title>
            
            {/* Facebook Post Mockup */}
            <div style={{ 
              background: '#fff', 
              borderRadius: 8, 
              boxShadow: '0 1px 2px rgba(0,0,0,0.2)', 
              maxWidth: 500, 
              margin: '0 auto',
              padding: '12px 16px'
            }}>
              {/* Post Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                <Space>
                  <Avatar 
                    src={previewContent.selectedPage?.pictureUrl} 
                    size={40} 
                    style={{ border: '1px solid #ddd' }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, color: '#050505', lineHeight: '1.2' }}>
                      {previewContent.selectedPage?.name || 'Tên trang Facebook'}
                    </div>
                    <Space size={4} style={{ fontSize: 13, color: '#65676B' }}>
                      <span>Vừa xong</span>
                      <span>·</span>
                      <GlobalOutlined style={{ fontSize: 12 }} />
                    </Space>
                  </div>
                </Space>
                <EllipsisOutlined style={{ fontSize: 20, color: '#65676B', cursor: 'pointer' }} />
              </div>

              {/* Post Content */}
              <div style={{ 
                color: '#050505', 
                fontSize: 15, 
                marginBottom: 12, 
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {previewContent.message || 'Nội dung bài viết sẽ hiển thị ở đây...'}
              </div>

              {/* Post Media Preview */}
              {(previewContent.type === PostType.PHOTO || previewContent.type === PostType.VIDEO) && (
                <div style={{ margin: '0 -16px 12px', background: '#f0f2f5', minHeight: 100 }}>
                    {fileList.length > 0 ? (
                        <div style={{ display: 'grid', gridTemplateColumns: fileList.length > 1 ? '1fr 1fr' : '1fr', gap: 2 }}>
                            {fileList.slice(0, 4).map((file, idx) => (
                                <div key={file.uid} style={{ position: 'relative', paddingTop: fileList.length === 1 ? '56.25%' : '100%', background: '#eee' }}>
                                    {file.type === 'image' ? (
                                        <img src={file.previewUrl} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} alt="preview" />
                                    ) : (
                                        <video src={file.previewUrl} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} muted />
                                    )}
                                    {idx === 3 && fileList.length > 4 && (
                                        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.4)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 600 }}>
                                            +{fileList.length - 4}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : previewContent.mediaUrl ? (
                        previewContent.type === PostType.PHOTO ? (
                            <img src={previewContent.mediaUrl} style={{ width: '100%', display: 'block' }} alt="preview" />
                        ) : (
                            <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000' }}>
                                <PlayCircleOutlined style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#fff', fontSize: 48, opacity: 0.8 }} />
                            </div>
                        )
                    ) : null}
                </div>
              )}

              {previewContent.type === PostType.LINK && previewContent.link && (
                <div style={{ 
                  margin: '0 -16px 12px', 
                  borderTop: '1px solid #ddd', 
                  borderBottom: '1px solid #ddd',
                  background: '#f0f2f5',
                  padding: '12px',
                  cursor: 'pointer'
                }}>
                  <div style={{ color: '#65676B', fontSize: 12, textTransform: 'uppercase' }}>
                    {previewContent.link && new URL(previewContent.link).hostname}
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 16, color: '#050505', margin: '4px 0' }}>
                    Preview Title
                  </div>
                  <div style={{ color: '#65676B', fontSize: 14 }}>
                    Link preview description will appear here...
                  </div>
                </div>
              )}

              {/* Post Stats */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #CED0D4', fontSize: 14, color: '#65676B' }}>
                <Space><LikeOutlined /> 0</Space>
                <Space>
                  <span>0 lượt bình luận</span>
                  <span>0 lượt chia sẻ</span>
                </Space>
              </div>

              {/* Post Actions */}
              <div style={{ display: 'flex', paddingTop: 4 }}>
                <Button type="text" block icon={<LikeOutlined />} style={{ color: '#65676B', fontWeight: 600 }}>Thích</Button>
                <Button type="text" block icon={<CommentOutlined />} style={{ color: '#65676B', fontWeight: 600 }}>Bình luận</Button>
                <Button type="text" block icon={<ShareAltOutlined />} style={{ color: '#65676B', fontWeight: 600 }}>Chia sẻ</Button>
              </div>
            </div>
            
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Text type="secondary" italic style={{ fontSize: 12 }}>
                Lưu ý: Đây chỉ là giao diện xem trước mô phỏng. Hiển thị thực tế trên Facebook có thể khác nhau.
              </Text>
            </div>
          </div>
        </Col>
      </Row>

      <Modal
        title={<Space><CommentOutlined /><span>Cài đặt tự động bình luận</span></Space>}
        open={showCommentModal}
        onOk={() => setShowCommentModal(false)}
        onCancel={() => setShowCommentModal(false)}
        width={isMobile ? '95%' : 700}
        okText="Hoàn tất"
        cancelButtonProps={{ style: { display: 'none' } }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">Cài đặt các bình luận sẽ được đăng tự động sau khi bài viết được tạo thành công.</Text>
        </div>

        <div style={{ marginBottom: 20 }}>
          <Text strong>Khoảng cách giữa các bình luận (giây):</Text>
          <div style={{ marginTop: 8 }}>
            <InputNumber 
              min={1} 
              max={3600} 
              value={commentDelay} 
              onChange={val => setCommentDelay(val || 10)} 
              addonAfter="giây"
            />
          </div>
        </div>

        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab={<Space><EditOutlined />Nhập thủ công</Space>} key="1">
            <div style={{ maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
              {comments.map((comment, index) => (
                <div key={index} style={{ marginBottom: 16, padding: 12, border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <TextArea 
                        placeholder={`Bình luận ${index + 1}...`} 
                        autoSize={{ minRows: 2 }}
                        value={comment.text}
                        onChange={e => updateComment(index, e.target.value)}
                        style={{ borderRadius: 6 }}
                      />
                    </div>
                    <Button 
                      danger 
                      type="text"
                      icon={<DeleteOutlined />} 
                      onClick={() => removeCommentField(index)}
                      disabled={comments.length === 1 && !comment.text && !comment.image}
                    />
                  </div>
                  
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      beforeUpload={(file) => handleCommentImageUpload(index, file)}
                    >
                      <Button size="small" icon={<CameraOutlined />}>
                        {comment.image ? 'Đổi ảnh' : 'Thêm ảnh'}
                      </Button>
                    </Upload>

                    {comment.previewUrl && (
                      <div style={{ position: 'relative', width: 40, height: 40, borderRadius: 4, overflow: 'hidden', border: '1px solid #d9d9d9' }}>
                        <img src={comment.previewUrl} alt="preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <div 
                          style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(0,0,0,0.5)', cursor: 'pointer', display: 'flex' }}
                          onClick={() => {
                            const newComments = [...comments];
                            if (newComments[index].previewUrl) URL.revokeObjectURL(newComments[index].previewUrl!);
                            newComments[index].image = undefined;
                            newComments[index].previewUrl = undefined;
                            setComments(newComments);
                          }}
                        >
                          <CloseCircleFilled style={{ color: '#fff', fontSize: 12 }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <Button 
                type="dashed" 
                block 
                icon={<PlusOutlined />} 
                onClick={addCommentField}
                style={{ marginTop: 8 }}
              >
                Thêm bình luận mới
              </Button>
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane tab={<Space><FileTextOutlined />Nhập từ Excel</Space>} key="2">
             <div style={{ padding: '20px 0' }}>
               <div style={{ marginBottom: 16, textAlign: 'right' }}>
                 <Button 
                   type="link" 
                   icon={<DownloadOutlined />} 
                   onClick={downloadSampleExcel}
                   style={{ padding: 0 }}
                 >
                   Tải file mẫu (.xlsx)
                 </Button>
               </div>

               <Upload.Dragger
                 accept=".xlsx, .xls, .csv"
                 showUploadList={false}
                 beforeUpload={handleExcelImport}
                 height={200}
               >
                 <p className="ant-upload-drag-icon">
                   <InboxOutlined style={{ color: '#1890ff' }} />
                 </p>
                 <p className="ant-upload-text">Nhấp hoặc kéo thả file Excel vào đây</p>
               </Upload.Dragger>
               
               {importedFileName && (
                 <div style={{ marginTop: 16, padding: '8px 12px', background: '#e6f7ff', border: '1px solid #91d5ff', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Space>
                      <FileTextOutlined style={{ color: '#1890ff' }} />
                      <Text strong>{importedFileName}</Text>
                      <Text type="secondary">({comments.length} bình luận)</Text>
                    </Space>
                    <Button 
                      type="text" 
                      danger 
                      size="small" 
                      onClick={() => {
                        setComments([{ text: '' }]);
                        setImportedFileName(null);
                      }}
                    >
                      Xóa
                    </Button>
                 </div>
               )}
             </div>
          </Tabs.TabPane>
        </Tabs>
      </Modal>
    </div>
  );
};

export default CreatePost;
